import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Product } from '../../types/product';
import { useWebCategories } from '../../hooks/useWebCategories';
import toast, { Toaster } from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  CheckIcon,
  XMarkIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const ProductCategoryAssignmentList: React.FC = () => {
  const { mainCategories, loading: categoriesLoading } = useWebCategories();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]); // Multi-select kategóriák
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [showCategoryStats, setShowCategoryStats] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [oldFormatCount, setOldFormatCount] = useState(0);

  // Termékek betöltése
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const productsRef = collection(db, 'products');
      const snapshot = await getDocs(productsRef);
      
      const productsList: Product[] = [];
      snapshot.forEach((doc) => {
        productsList.push({ id: doc.id, ...doc.data() } as Product);
      });

      setProducts(productsList);
      
      // Számoljuk meg a régi formátumú termékeket
      const oldFormat = productsList.filter(p => p.webCategoryId && !p.webCategoryIds);
      setOldFormatCount(oldFormat.length);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper: termék kategóriáinak lekérése (backward compatibility)
  const getProductCategories = (product: Product): string[] => {
    if (product.webCategoryIds && product.webCategoryIds.length > 0) {
      return product.webCategoryIds;
    }
    if (product.webCategoryId) {
      return [product.webCategoryId];
    }
    return [];
  };

  // Szűrt termékek
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterCategory === 'all') return matchesSearch;
    
    const categories = getProductCategories(product);
    if (filterCategory === 'uncategorized') return matchesSearch && categories.length === 0;
    return matchesSearch && categories.includes(filterCategory);
  });

  // Termék kiválasztása/törlése
  const toggleProduct = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
    updateSelectedCategoriesFromProducts(newSelected);
  };

  // Összes látható termék kiválasztása
  const selectAll = () => {
    const newSelected = new Set(filteredProducts.map(p => p.id));
    setSelectedProducts(newSelected);
    updateSelectedCategoriesFromProducts(newSelected);
  };

  // Kijelölés törlése
  const clearSelection = () => {
    setSelectedProducts(new Set());
    setSelectedCategories([]);
  };

  // Kiválasztott termékek közös kategóriáinak frissítése
  const updateSelectedCategoriesFromProducts = (selectedProductIds: Set<string>) => {
    if (selectedProductIds.size === 0) {
      setSelectedCategories([]);
      return;
    }

    // Gyűjtsük össze az összes kiválasztott termék kategóriáit
    const allCategories = new Set<string>();
    const categoryCount: Record<string, number> = {};

    selectedProductIds.forEach(productId => {
      const product = products.find(p => p.id === productId);
      if (product) {
        const productCategories = getProductCategories(product);
        productCategories.forEach(catId => {
          allCategories.add(catId);
          categoryCount[catId] = (categoryCount[catId] || 0) + 1;
        });
      }
    });

    // Csak azokat a kategóriákat válasszuk ki, amik legalább egy terméknél megtalálhatók
    // (ha több termék van kiválasztva, a közös kategóriákat mutatjuk)
    const commonCategories = Array.from(allCategories);
    setSelectedCategories(commonCategories);
  };

  // Régi formátum migrálása: webCategoryId -> webCategoryIds
  const migrateOldFormat = async () => {
    try {
      setMigrating(true);
      
      // Keresük a régi formátumú termékeket
      const productsToMigrate = products.filter(p => p.webCategoryId && !p.webCategoryIds);
      
      if (productsToMigrate.length === 0) {
        toast.success('Nincs migrálandó termék! ✅', {
          duration: 3000,
          position: 'top-right',
        });
        return;
      }
      
      const updatePromises = productsToMigrate.map(product => {
        const productRef = doc(db, 'products', product.id);
        return updateDoc(productRef, {
          webCategoryIds: [product.webCategoryId!],
          webCategoryId: null // Töröljük a régi mezőt
        });
      });
      
      await Promise.all(updatePromises);
      
      // Frissítjük a helyi state-et
      setProducts(products.map(p => {
        if (p.webCategoryId && !p.webCategoryIds) {
          return {
            ...p,
            webCategoryIds: [p.webCategoryId],
            webCategoryId: undefined
          };
        }
        return p;
      }));
      
      setOldFormatCount(0);
      
      toast.success(`${productsToMigrate.length} termék sikeresen migrálva az új formátumra! 🎉`, {
        duration: 4000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error migrating products:', error);
      toast.error('Hiba történt a migrálás során! ❌', {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setMigrating(false);
    }
  };

  // Kategória statisztika számítása
  const categoryStats = useMemo(() => {
    const stats: Record<string, number> = {};
    products.forEach(product => {
      const categories = getProductCategories(product);
      categories.forEach(catId => {
        stats[catId] = (stats[catId] || 0) + 1;
      });
    });
    return stats;
  }, [products]);

  // Több kategória hozzáadása egyszerre
  const assignMultipleCategories = async () => {
    if (selectedCategories.length === 0 || selectedProducts.size === 0) return;

    try {
      setSaving(true);
      const updatePromises = Array.from(selectedProducts).map(productId => {
        const product = products.find(p => p.id === productId);
        if (!product) return Promise.resolve();
        
        const currentCategories = getProductCategories(product);
        
        // Csak azokat adjuk hozzá, amik még nincsenek benne
        const newCategoriesToAdd = selectedCategories.filter(cat => !currentCategories.includes(cat));
        if (newCategoriesToAdd.length === 0) return Promise.resolve();
        
        const newCategories = [...currentCategories, ...newCategoriesToAdd];
        const productRef = doc(db, 'products', productId);
        return updateDoc(productRef, { 
          webCategoryIds: newCategories,
          webCategoryId: null
        });
      });

      await Promise.all(updatePromises);
      
      // Frissítjük a helyi state-et
      setProducts(products.map(p => {
        if (!selectedProducts.has(p.id)) return p;
        
        const currentCategories = getProductCategories(p);
        const newCategoriesToAdd = selectedCategories.filter(cat => !currentCategories.includes(cat));
        if (newCategoriesToAdd.length === 0) return p;
        
        return { 
          ...p, 
          webCategoryIds: [...currentCategories, ...newCategoriesToAdd],
          webCategoryId: undefined
        };
      }));

      clearSelection();
      toast.success(`${selectedCategories.length} kategória hozzáadva ${selectedProducts.size} termékhez! 🎉`, {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error assigning categories:', error);
      toast.error('Hiba történt a kategorizálás során! ❌', {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  // Kategória hozzárendelése a kiválasztott termékekhez (hozzáadás a meglévőkhöz)
  const assignCategory = async () => {
    if (!selectedCategory || selectedProducts.size === 0) return;

    try {
      setSaving(true);
      const updatePromises = Array.from(selectedProducts).map(productId => {
        const product = products.find(p => p.id === productId);
        if (!product) return Promise.resolve();
        
        const currentCategories = getProductCategories(product);
        
        // Ha már benne van, ne adjuk hozzá újra
        if (currentCategories.includes(selectedCategory)) {
          return Promise.resolve();
        }
        
        const newCategories = [...currentCategories, selectedCategory];
        const productRef = doc(db, 'products', productId);
        return updateDoc(productRef, { 
          webCategoryIds: newCategories,
          webCategoryId: null // deprecated mező törlése
        });
      });

      await Promise.all(updatePromises);
      
      // Frissítjük a helyi state-et
      setProducts(products.map(p => {
        if (!selectedProducts.has(p.id)) return p;
        
        const currentCategories = getProductCategories(p);
        if (currentCategories.includes(selectedCategory)) return p;
        
        return { 
          ...p, 
          webCategoryIds: [...currentCategories, selectedCategory],
          webCategoryId: undefined
        };
      }));

      clearSelection();
      toast.success(`${selectedProducts.size} termékhez hozzáadva a kategória! 🎉`, {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error assigning category:', error);
      toast.error('Hiba történt a kategorizálás során! ❌', {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  // Több kategória eltávolítása egyszerre
  const removeMultipleCategories = async () => {
    if (selectedCategories.length === 0 || selectedProducts.size === 0) return;

    try {
      setSaving(true);
      const updatePromises = Array.from(selectedProducts).map(productId => {
        const product = products.find(p => p.id === productId);
        if (!product) return Promise.resolve();
        
        const currentCategories = getProductCategories(product);
        const newCategories = currentCategories.filter(id => !selectedCategories.includes(id));
        
        const productRef = doc(db, 'products', productId);
        return updateDoc(productRef, { 
          webCategoryIds: newCategories.length > 0 ? newCategories : null,
          webCategoryId: null
        });
      });

      await Promise.all(updatePromises);
      
      // Frissítjük a helyi state-et
      setProducts(products.map(p => {
        if (!selectedProducts.has(p.id)) return p;
        
        const currentCategories = getProductCategories(p);
        const newCategories = currentCategories.filter(id => !selectedCategories.includes(id));
        
        return { 
          ...p, 
          webCategoryIds: newCategories.length > 0 ? newCategories : undefined,
          webCategoryId: undefined
        };
      }));

      clearSelection();
      toast.success(`${selectedCategories.length} kategória eltávolítva ${selectedProducts.size} termékről! ✅`, {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error removing categories:', error);
      toast.error('Hiba történt a kategóriák eltávolítása során! ❌', {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  // Kategória hozzáadása/eltávolítása a multi-select listából
  const toggleCategorySelection = (categoryId: string) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Kategória eltávolítása a kiválasztott termékekről
  const removeCategory = async () => {
    if (!selectedCategory || selectedProducts.size === 0) return;

    try {
      setSaving(true);
      const updatePromises = Array.from(selectedProducts).map(productId => {
        const product = products.find(p => p.id === productId);
        if (!product) return Promise.resolve();
        
        const currentCategories = getProductCategories(product);
        const newCategories = currentCategories.filter(id => id !== selectedCategory);
        
        const productRef = doc(db, 'products', productId);
        return updateDoc(productRef, { 
          webCategoryIds: newCategories.length > 0 ? newCategories : null,
          webCategoryId: null // deprecated mező törlése
        });
      });

      await Promise.all(updatePromises);
      
      // Frissítjük a helyi state-et
      setProducts(products.map(p => {
        if (!selectedProducts.has(p.id)) return p;
        
        const currentCategories = getProductCategories(p);
        const newCategories = currentCategories.filter(id => id !== selectedCategory);
        
        return { 
          ...p, 
          webCategoryIds: newCategories.length > 0 ? newCategories : undefined,
          webCategoryId: undefined
        };
      }));

      clearSelection();
      toast.success(`Kategória eltávolítva ${selectedProducts.size} termékről! ✅`, {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error removing category:', error);
      toast.error('Hiba történt a kategória eltávolítása során! ❌', {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  // Összes kategória eltávolítása a kiválasztott termékekről
  const removeAllCategories = async () => {
    if (selectedProducts.size === 0) return;

    try {
      setSaving(true);
      const updatePromises = Array.from(selectedProducts).map(productId => {
        const productRef = doc(db, 'products', productId);
        return updateDoc(productRef, { 
          webCategoryIds: null,
          webCategoryId: null
        });
      });

      await Promise.all(updatePromises);
      
      // Frissítjük a helyi state-et
      setProducts(products.map(p => 
        selectedProducts.has(p.id) 
          ? { ...p, webCategoryIds: undefined, webCategoryId: undefined }
          : p
      ));

      clearSelection();
      toast.success(`Minden kategória eltávolítva ${selectedProducts.size} termékről! ✅`, {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error removing categories:', error);
      toast.error('Hiba történt a kategóriák eltávolítása során! ❌', {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  // Kategória név lekérése ID alapján
  const getCategoryName = (categoryId?: string) => {
    if (!categoryId) return '-';
    
    for (const mainCat of mainCategories) {
      if (mainCat.id === categoryId) return mainCat.name;
      if (mainCat.subcategories) {
        const subCat = mainCat.subcategories.find(sub => sub.id === categoryId);
        if (subCat) return `${mainCat.name} > ${subCat.name}`;
      }
    }
    return '-';
  };

  if (loading || categoriesLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster />
      
      {/* Vezérlő panel */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 p-6 space-y-4">
        {/* Gyors kategória szűrők (chipek) */}
        <div className="flex flex-wrap gap-2 pb-4 border-b border-gray-200">
          <button
            onClick={() => setFilterCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              filterCategory === 'all'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Összes ({products.length})
          </button>
          <button
            onClick={() => setFilterCategory('uncategorized')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
              filterCategory === 'uncategorized'
                ? 'bg-orange-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Kategorizálatlan ({products.filter(p => getProductCategories(p).length === 0).length})
          </button>
          {mainCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setFilterCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                filterCategory === cat.id
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.name} ({categoryStats[cat.id] || 0})
            </button>
          ))}
          <button
            onClick={() => setShowCategoryStats(!showCategoryStats)}
            className="ml-auto px-4 py-2 rounded-full text-sm font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 transition-all"
          >
            {showCategoryStats ? '📊 Statisztika elrejtése' : '📊 Statisztika'}
          </button>
          
          {/* Migrációs gomb, ha van régi formátumú termék */}
          {oldFormatCount > 0 && (
            <button
              onClick={migrateOldFormat}
              disabled={migrating}
              className="px-4 py-2 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition-all border-2 border-yellow-300 shadow-md"
            >
              {migrating ? '⏳ Migrálás...' : `🔄 ${oldFormatCount} régi termék migrálása`}
            </button>
          )}
        </div>

        {/* Kategória statisztika */}
        {showCategoryStats && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-5">
            <h3 className="text-lg font-bold text-purple-900 mb-3">📊 Kategória statisztika</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {mainCategories.map(cat => (
                <div key={cat.id} className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-sm font-semibold text-gray-900">{cat.name}</div>
                  <div className="text-2xl font-bold text-purple-600">{categoryStats[cat.id] || 0}</div>
                  {cat.subcategories && cat.subcategories.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {cat.subcategories.map(sub => (
                        <div key={sub.id} className="text-xs text-gray-600 flex justify-between">
                          <span>{sub.name}</span>
                          <span className="font-semibold">{categoryStats[sub.id] || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Keresés és szűrés */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Keresés név vagy kód alapján..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="all">Összes termék</option>
              <option value="uncategorized">Kategorizálatlan</option>
              {mainCategories.map(cat => (
                <React.Fragment key={cat.id}>
                  <option value={cat.id}>{cat.name}</option>
                  {cat.subcategories?.map(sub => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </React.Fragment>
              ))}
            </select>
          </div>

          <button
            onClick={fetchProducts}
            className="flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Frissítés
          </button>
        </div>

        {/* Kijelölés és kategorizálás */}
        {selectedProducts.size > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                  {selectedProducts.size}
                </div>
                <span className="text-sm font-bold text-blue-900">
                  termék kiválasztva
                </span>
              </div>
              <button
                onClick={clearSelection}
                className="text-sm text-blue-600 hover:text-blue-800 font-semibold flex items-center space-x-1"
              >
                <XMarkIcon className="h-4 w-4" />
                <span>Kijelölés törlése</span>
              </button>
            </div>

            {/* Multi-select kategória választó */}
            <div className="mb-4">
              <label className="block text-sm font-bold text-blue-900 mb-2">
                Válassz kategóriákat (több is kiválasztható):
              </label>
              <div className="max-h-60 overflow-y-auto p-3 bg-white rounded-lg border border-blue-200 space-y-3">
                {mainCategories.map(cat => (
                  <div key={cat.id} className="border-l-4 border-blue-400 pl-3 py-2 bg-blue-50 rounded">
                    {/* Főkategória */}
                    <label className="flex items-center space-x-2 p-2 rounded hover:bg-blue-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat.id)}
                        onChange={() => toggleCategorySelection(cat.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="text-sm font-bold text-blue-900">{cat.name}</span>
                    </label>
                    
                    {/* Alkategóriák */}
                    {cat.subcategories && cat.subcategories.length > 0 && (
                      <div className="ml-6 mt-1 space-y-1">
                        {cat.subcategories.map(sub => (
                          <label key={sub.id} className="flex items-center space-x-2 p-2 rounded hover:bg-white cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(sub.id)}
                              onChange={() => toggleCategorySelection(sub.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">{sub.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {selectedCategories.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-sm font-semibold text-blue-900">Kiválasztva:</span>
                  {selectedCategories.map(catId => (
                    <span key={catId} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      {getCategoryName(catId)}
                      <button
                        onClick={() => toggleCategorySelection(catId)}
                        className="ml-2 hover:text-blue-900"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <button
                    onClick={() => setSelectedCategories([])}
                    className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                  >
                    Összes törlése
                  </button>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">

              <button
                onClick={assignMultipleCategories}
                disabled={selectedCategories.length === 0 || saving}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed font-bold shadow-lg hover:shadow-xl transition-all flex items-center justify-center space-x-2"
              >
                <CheckIcon className="h-5 w-5" />
                <span>{saving ? 'Mentés...' : `${selectedCategories.length} kategória hozzáadása`}</span>
              </button>

              <button
                onClick={removeMultipleCategories}
                disabled={selectedCategories.length === 0 || saving}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-xl hover:from-orange-700 hover:to-orange-800 disabled:opacity-50 disabled:cursor-not-allowed font-bold shadow-lg hover:shadow-xl transition-all flex items-center justify-center space-x-2"
              >
                <XMarkIcon className="h-5 w-5" />
                <span>{selectedCategories.length} kategória eltávolítása</span>
              </button>

              <button
                onClick={removeAllCategories}
                disabled={saving}
                className="px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed font-bold shadow-lg hover:shadow-xl transition-all flex items-center space-x-2"
              >
                <XMarkIcon className="h-5 w-5" />
                <span>Összes törlése</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Termékek listája */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 rounded-lg p-2">
              <CheckIcon className="h-5 w-5 text-white" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">
              Termékek <span className="text-blue-600">({filteredProducts.length})</span>
            </h3>
          </div>
          <button
            onClick={selectAll}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold text-sm shadow-md hover:shadow-lg transition-all"
          >
            Összes kijelölése
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
              <tr>
                <th className="w-12 px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedProducts.size === filteredProducts.length && filteredProducts.length > 0}
                    onChange={(e) => e.target.checked ? selectAll() : clearSelection()}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Termékkód
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Terméknév
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Jelenlegi kategória
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Ár
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    Nincs megjeleníthető termék
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr 
                    key={product.id}
                    className={`hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 cursor-pointer transition-all border-l-4 ${
                      selectedProducts.has(product.id) 
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-l-blue-600' 
                        : 'border-l-transparent'
                    }`}
                    onClick={() => toggleProduct(product.id)}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProducts.has(product.id)}
                        onChange={() => toggleProduct(product.id)}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-gray-900 bg-gray-100 px-3 py-1 rounded-lg">
                        {product.code}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-gray-900">
                        {product.name}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const categories = getProductCategories(product);
                        if (categories.length === 0) {
                          return (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                              Nincs kategória
                            </span>
                          );
                        }
                        return (
                          <div className="flex flex-wrap gap-1">
                            {categories.map(catId => (
                              <span key={catId} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                {getCategoryName(catId)}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-blue-600">
                        {product.netPrice ? `${product.netPrice.toLocaleString()} Ft` : '-'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductCategoryAssignmentList;
